import { Box, Center, Flex, Heading } from '@chakra-ui/layout';
import { Button, Spinner, useToast, useDisclosure } from '@chakra-ui/react';

import { useKeycloak } from '@react-keycloak/web';
import { Link, useNavigate } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import SelectedGameContext from '../context/SelectedGameContext';
import config from '../config';
import DataTable from '../components/DataTable';
import DataCaller from '../util/dataCaller';
import { FaEdit, FaTrash, FaAward } from 'react-icons/fa';
import { ConfirmationAlert } from '../components/ConfirmationAlert';
import {
    ProfileError,
    getProfileErrorText,
    getProfileErrorHeader,
} from '../context/ProfileContext';

import * as LBCONFIG from '../consts/LeaderboardConfig';

function jsonToArray(json: any): Array<{ [key: string]: any }> {
    const result = [];
    for (const prop in json) {
        if (json.hasOwnProperty(prop)) {
            result.push(json[prop]);
        }
    }
    return result;
}

export const LeaderboardsConfigs: React.FC<{}> = () => {
    const selectedGame = useContext(SelectedGameContext);
    const currLicense = selectedGame?.currLicense;
    const toast = useToast();
    const [configs, setConfigs] = useState<Array<{ [key: string]: any }>>();
    const [configsLoaded, setConfigsLoaded] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState('');

    const [lastDeletedConfig, setLastDeletedConfig] = useState('');
    const navigate = useNavigate();

    const { keycloak } = useKeycloak();
    const [errors] = useState<ProfileError[]>([]);

    // hook used with alert confirmation
    const { isOpen: isConfirmationOpen, onOpen, onClose } = useDisclosure();

    const url = `${config.leaderboardServiceURL}/game/${currLicense?.key}/leaderboard`;

    // dataCaller util to handle data requests
    const dataCaller = new DataCaller(url, keycloak.token!);

    const goToEdit = (configName: string) => {
        navigate(`edit-leaderboard-config/${configName}`);
    };

    const deleteConfig = async (test: any) => {
        const configName = selectedConfig;
        setConfigsLoaded(false);
        const res = await dataCaller.delete(configName);

        if (res.status !== 200) {
            setConfigsLoaded(true);
            if (res.status === 404) {
                setConfigs([]);
                return;
            }
            return toast({
                title: 'Error',
                description: res.message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        }
        setLastDeletedConfig(configName);
        setConfigsLoaded(true);
        return toast({
            title: 'Success',
            description: 'Leaderboard Config removed',
            status: 'success',
            duration: 9000,
            isClosable: true,
        });
    };

    const confirmOpen = (selected: any) => {
        setSelectedConfig(selected.name);
        onOpen();
    };

    // Configure edit and delete callbacks on the DataTable
    const dataTableCallbacks = [
        {
            label: 'Edit',
            callback: (config: any) => {
                goToEdit(config.name);
            },
            icon: <FaEdit />,
            colour: 'blue',
        },
        {
            label: 'Delete',
            callback: confirmOpen,
            icon: <FaTrash />,
            colour: 'red',
        },
    ];
    // Configure columns to display in the list
    const columnsToDisplay = [
        { name: 'Name', key: 'name' },
        { name: 'Order', key: 'order' },
        { name: 'Score Type', key: 'score_config.type' },
    ];

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    // configure ConfirmationAlert to display a delete confirmation
    const confirmationButtons = [
        {
            label: 'Cancel',
            isCancel: true,
            useCloseCallBack: true,
            callback: () => {
                setSelectedConfig('');
            },
        },
        {
            label: 'Delete',
            callback: () => deleteConfig(selectedConfig),
            useCloseCallBack: true,
            isCancel: false,
        },
    ];

    const renderLeaderboardConfigs = () => {
        if (!configs || configs.length === 0) {
            return <Center>{LBCONFIG.LEADERBOARD_CONFIGS_NO_LEADERBOARDS}</Center>;
        }

        return (
            <>
                <DataTable
                    data={configs}
                    columns={columnsToDisplay}
                    actionItems={dataTableCallbacks}
                ></DataTable>
                <ConfirmationAlert
                    isConfirmOpen={isConfirmationOpen}
                    prompt={`Delete ${selectedConfig} config?`}
                    title="Delete Leaderboard Config"
                    onOpenCallback={onOpen}
                    onCloseCallback={onClose}
                    options={confirmationButtons}
                ></ConfirmationAlert>
            </>
        );
    };

    useEffect(() => {
        if (!currLicense) {
            return;
        }

        const getConfigs = async () => {
            const dc = new DataCaller(url, keycloak.token!);
            const data = await dc.getAll();
            if (data.status !== 200) {
                setConfigsLoaded(true);
                if (data.status === 404) {
                    setConfigs([]);

                    return;
                }
                return;
            }

            const result = await data.json();
            const lbConfigArray = jsonToArray(result.data.leaderboards);

            setConfigs(lbConfigArray);
            setConfigsLoaded(true);
        };
        getConfigs();
    }, [currLicense, lastDeletedConfig, keycloak.token, url]);

    return (
        <Box>
            <Heading>{LBCONFIG.LEADERBOARD_CONFIGS_HEADING}</Heading>
            <Box mt={3} mb={3} mr={3}>
                <Heading as="h5" size={'md'}>
                    Create, Edit and Remove your Leaderboard Configurations
                </Heading>
            </Box>
            <br />
            {errors.length > 0 ? (
                <>
                    <Heading textAlign="center" marginBottom="1em">
                        Oh No! Error Detected
                    </Heading>
                    {renderError()}
                </>
            ) : (
                <Box>
                    <Flex justifyContent={'space-between'}>
                        <Heading as="h2" size="md">
                            {LBCONFIG.LEADERBOARD_CONFIGS_DATA_TABLE_TITLE}
                        </Heading>
                        <Flex>
                            {currLicense && (
                                <Link
                                    to={`/game/${currLicense?.key}/leaderboards/add-leaderboard-config`}
                                    data-cy="add-config"
                                >
                                    <Button leftIcon={<FaAward />}>
                                        {LBCONFIG.LEADERBOARD_CONFIGS_CREATE_BUTTON_LABEL}
                                    </Button>
                                </Link>
                            )}
                        </Flex>
                    </Flex>
                    {configsLoaded ? (
                        renderLeaderboardConfigs()
                    ) : (
                        <Center width="100%" height="100%">
                            <Spinner data-cy="loading" />
                        </Center>
                    )}
                </Box>
            )}
        </Box>
    );
};
